import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { ClientPaginatedTable } from 'components/table';
import * as TableConfig from './tablesConfig';
import { selectTranslations } from 'state/reducers/translation.reducers';
import React, { useEffect, useMemo, useState } from 'react';
import { customerActivityTypes } from 'constants/customer-account.constants';
import { getAllBenefits, getCompanyBenefits } from 'state/reducers/benefits.reducer';
import { addCompanyBenefit, getBenefits, getCompanyBenefitsByCompanyId } from 'services/benefits.service';
import { SelectList } from 'components/SelectList';
import { BenefitType, IBenefit } from 'constants/benefits.constants';
import { FormCard, FormCardLayout } from 'theme/elements/Forms';
import Add from '@mui/icons-material/Add';
import _ from 'lodash';
import { permissionEnums } from 'constants/permission.constants';
import { hasPermission } from 'components/AuthorizationComponent';

export const CompanyBenefitsTable = (props: { companyId: string }) => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const canCreateBenefit = hasPermission([permissionEnums.ASSIGN_COMPANY_BENEFIT]);
	const [tableData, setTableData] = useState<Record<string, any>[]>([]);
	const companyBenefits = useAppSelector(getCompanyBenefits);
	const benefits = useAppSelector(getAllBenefits);

	const [addedBenefit, setAddedBenefit] = useState<{ value: number; name: string }>({ value: 0, name: '' });

	const addNewCompanyBenefit = async () => {
		await dispatch(addCompanyBenefit(addedBenefit.value, props.companyId));
		setAddedBenefit({ value: 0, name: '' });
	};
	const companyBenefitsByType = useMemo(() => {
		return {
			[BenefitType.PAYMENT]: companyBenefits.find(
				(companyBenefit: any) => companyBenefit.benefit.type === BenefitType.PAYMENT && companyBenefit.isActive
			),
			[BenefitType.CONVERT]: companyBenefits.find(
				(companyBenefit: any) => companyBenefit.benefit.type === BenefitType.CONVERT && companyBenefit.isActive
			),
			[BenefitType.PLAN]: companyBenefits.find(
				(companyBenefit: any) => companyBenefit.benefit.type === BenefitType.PLAN && companyBenefit.isActive
			),
		};
	}, [benefits, companyBenefits]);

	const benefitsOptions = useMemo(() => {
		if (!benefits?.length) return [];

		return benefits
			?.filter((benefit: any) => {
				const now = new Date();
				const isValid = now >= new Date(benefit?.startDate) && now <= new Date(benefit?.expirationDate);
				return isValid;
			})
			.map((benefit: IBenefit) => {
				return {
					value: benefit.id,
					name: benefit.name,
					disabled:
						!benefit.isActive ||
						!!companyBenefitsByType[
							_.startCase(_.camelCase(benefit.type)).replace(/ /g, '') as BenefitType
						],
				};
			});
	}, [benefits, companyBenefitsByType]);

	useEffect(() => {
		setTableData(companyBenefits || []);
	}, [companyBenefits]);

	return (
		<Box display="flex" flexDirection="column" gap={2}>
			<Typography variant="pageTitle" display={'flex'} flexDirection={'row'}>
				{t('page.benefits_management.title')}
			</Typography>
			{canCreateBenefit && (
				<Stack direction="row" gap="32px" alignItems="center" marginTop="8px">
					<SelectList
						label="שם ההטבה"
						options={benefitsOptions}
						selected={addedBenefit}
						styleObj={{
							minWidth: '460px',
							'& .MuiInputBase-root': {
								width: '100%',
							},
						}}
						handleChange={(event: any) => {
							setAddedBenefit(
								benefitsOptions.find(
									(item: { value: number; name: string }) => item.value === event.target.value
								)
							);
						}}
					/>
					<Button
						sx={{ height: '32px', borderRadius: '4px' }}
						variant="contained"
						startIcon={<Add />}
						onClick={addNewCompanyBenefit}
					>
						{t('page.customerAccounts.tables.companyBenefits.buttons.add')}
					</Button>
				</Stack>
			)}
			<Box>
				<FormCard key={0}>
					<ClientPaginatedTable
						rowsClass="alignTop"
						data={TableConfig.tableData(tableData)}
						defaultSortBy="createdAt"
						defaultOrderBy="desc"
						columns={TableConfig.buildColumns(t, dispatch, canCreateBenefit)}
					/>
				</FormCard>
			</Box>
		</Box>
	);
};
